import {
  Area,
  AreaChart,
  ReferenceLine,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";

import { BenchmarkLine } from "../../types/DashboardInterfaces";
import { isValidChartQuery } from "../../utils/DashboardUtils";
import { TickFormatter } from "../../utils/ChartUtils";
import useChartData from "./WidgetHooks/useChartData";
import ChartTooltip from "./Tooltips/ChartTooltip";
import { BaseChartProps } from "./WidgetTypes";
import useThemeColors from "./useThemeColors";
import useModule from "../../hooks/useModule";
import WidgetLoading from "./WidgetLoading";
import WidgetError from "./WidgetError";

// AKA AreaChart
const FilledLineChartWidget = ({
  section,
  setSettings,
  record,
  theme,
  topLevelFilters,
  viewFilters,
}: BaseChartProps) => {
  const { module } = useModule(section?.ModuleAPIName);

  const {
    chartData,
    chartError,
    chartLoading,
    groupByField,
    groupByFieldName,
    groupByFieldType,
    aggregationField,
    aggregationFieldName,
    aggregationChartColor,
    aggregationMethod,
    benchmarkLines,
  } = useChartData({
    moduleAPIName: section?.ModuleAPIName,
    query: section?.Query,
    validator: isValidChartQuery,
    orderBy: section?.OrderBy ?? null,
    record: record,
    topLevelFilters: topLevelFilters,
    viewFilters: viewFilters,
    queryKeyID: section?.id,
  });
  const { colors } = useThemeColors({ theme, chartData });

  const xAxis = groupByField;

  if (chartError) {
    return (
      <WidgetError
        onOpenSettings={setSettings ? () => setSettings(section) : null}
      />
    );
  }

  if (chartLoading) {
    return <WidgetLoading />;
  }

  return (
    <ResponsiveContainer width="100%" height="100%">
      <AreaChart
        data={chartData}
        margin={{
          top: 10,
          right: 30,
          left: 0,
          bottom: 0,
        }}
      >
        <XAxis
          name={groupByField}
          dataKey={xAxis}
          type="category"
          fontSize={14}
          tickLine={false}
          axisLine={true}
          fontWeight={500}
          tickFormatter={(value) =>
            TickFormatter({
              value,
              groupByFieldName,
              groupByField,
              groupByFieldType,
              chartData,
            })
          }
        />
        <YAxis
          tickLine={false}
          fontSize={14}
          axisLine={true}
          fontWeight={500}
        />
        <Tooltip
          content={
            <ChartTooltip
              aggregationField={aggregationField}
              aggregationMethod={aggregationMethod}
              groupByField={groupByField}
              groupByFieldName={groupByFieldName}
              aggregationFieldName={aggregationFieldName}
              moduleName={module?.Name ?? ""}
            />
          }
        />
        <Area
          type="monotone"
          dataKey={aggregationMethod}
          fill={aggregationChartColor || colors?.[0] || "#26b058"}
          stroke={aggregationChartColor || colors?.[0] || "#26b058"}
        />
        {benchmarkLines?.map((benchmarkLine: BenchmarkLine) => {
          return (
            <ReferenceLine
              key={benchmarkLine.id}
              y={benchmarkLine.Value}
              label={{
                value: benchmarkLine.Label,
                fontSize: 14,
                fontWeight: 600,
                position: "top",
              }}
              stroke={benchmarkLine.Color}
              strokeDasharray="3 3"
              fontSize={14}
              fontWeight={600}
            />
          );
        })}
      </AreaChart>
    </ResponsiveContainer>
  );
};

export default FilledLineChartWidget;
